import { useEffect, useState } from "react";

import clsx from "clsx";
import { Loader2 } from "lucide-react";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { PractitionerSelector } from "~/components/upload/practitioner-selector";
import { useUserProfile } from "~/lib/user";

import { Button } from "../ui/button";
import { ClientSelector } from "../upload/client-selector";

// AIDEV-NOTE: Types for client reassignment confirmation dialog
type ClientChangeType =
    | "client-to-client"
    | "client-to-other"
    | "other-to-client"
    | null;

// SessionParticipants component for displaying practitioner and client names
interface SessionParticipantsProps {
    practitionerId?: string | null;
    practitionerEmail?: string | null;
    clientId?: string | null;
    clientEmail?: string | null;
    /** Display name for a client with no email (practitioner-created placeholders,
     *  and the shared onboarding demo client). */
    clientName?: string | null;
    onSubmit: ({
        practitionerId,
        clientId,
    }: {
        practitionerId: string | null;
        practitionerEmail?: string | null;
        clientId: string | null;
        clientEmail?: string | null;
    }) => Promise<unknown>;
    /** If true, the participants are read-only (for public session access) */
    isPublicSession?: boolean;
    /** If true, the participants are read-only for another reason — the shared
     *  example session, where reassigning the client would affect everyone. */
    readOnly?: boolean;
}

export const SessionParticipants: React.FC<SessionParticipantsProps> = ({
    practitionerId,
    practitionerEmail,
    clientId,
    clientEmail,
    clientName,
    onSubmit,
    isPublicSession = false,
    readOnly = false,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tempPractitionerId, setTempPractitionerId] = useState<string>(
        practitionerId ?? "other",
    );
    const [tempClientId, setTempClientId] = useState<string>(
        clientId ?? "other",
    );
    const [tempPractitionerEmail, setTempPractitionerEmail] = useState<
        string | null
    >(practitionerEmail ?? null);
    const [tempClientEmail, setTempClientEmail] = useState<string | null>(
        clientEmail ?? null,
    );
    // AIDEV-NOTE: State for client reassignment confirmation dialog
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [clientChangeType, setClientChangeType] =
        useState<ClientChangeType>(null);

    const { data: userProfile } = useUserProfile();
    const userId = userProfile?.id;

    const selectorPractitionerId = !tempPractitionerId
        ? "other"
        : tempPractitionerId === userId
          ? "self"
          : tempPractitionerId;

    const selectorClientId = !tempClientId
        ? "other"
        : tempClientId === userId
          ? "self"
          : tempClientId;

    const openDialog = () => {
        // Reset temp values to current values when opening
        setTempPractitionerId(practitionerId ?? "other");
        setTempClientId(clientId ?? "other");
        setTempPractitionerEmail(practitionerEmail ?? null);
        setTempClientEmail(clientEmail ?? null);
        setIsDialogOpen(true);
    };

    // AIDEV-NOTE: Determine what type of client change is happening
    const getClientChangeType = (): ClientChangeType => {
        const oldClientIsOther = !clientId || clientId === "other";
        const newClientIsOther = !tempClientId || tempClientId === "other";

        if (oldClientIsOther && !newClientIsOther) {
            return "other-to-client";
        }
        if (!oldClientIsOther && newClientIsOther) {
            return "client-to-other";
        }
        if (
            !oldClientIsOther &&
            !newClientIsOther &&
            clientId !== tempClientId
        ) {
            return "client-to-client";
        }
        return null;
    };

    const handleSave = async () => {
        const hasChanges =
            tempPractitionerId !== practitionerId ||
            tempClientId !== clientId ||
            tempPractitionerEmail !== practitionerEmail ||
            tempClientEmail !== clientEmail;

        if (!hasChanges) {
            setIsDialogOpen(false);
            return;
        }

        // Check if client is changing and requires confirmation
        const changeType = getClientChangeType();
        if (changeType) {
            setClientChangeType(changeType);
            setShowConfirmation(true);
            return;
        }

        // No client change requiring confirmation, proceed directly
        await executeSubmit();
    };

    const executeSubmit = async () => {
        setIsLoading(true);
        await onSubmit({
            practitionerId: tempPractitionerId,
            practitionerEmail: tempPractitionerEmail,
            clientId: tempClientId,
            clientEmail: tempClientEmail,
        });
        setIsLoading(false);
        setIsDialogOpen(false);
        setShowConfirmation(false);
        setClientChangeType(null);
    };

    const handleConfirmationCancel = () => {
        setShowConfirmation(false);
        setClientChangeType(null);
    };

    // AIDEV-NOTE: Get the appropriate warning message based on change type
    const getConfirmationContent = (): {
        title: string;
        description: string;
    } => {
        const newClientDisplay = tempClientEmail || "the new client";
        switch (clientChangeType) {
            case "client-to-client":
                return {
                    title: "Reassign Session to Different Client",
                    description:
                        `You're about to reassign this session to ${newClientDisplay}.\n\nThis will:\n` +
                        "• Transfer all parts to the new client\n" +
                        "• Remove links to parts from other sessions (for privacy)\n" +
                        "• Delete part images (may regenerate based on new client's settings)\n\n" +
                        "This cannot be undone.",
                };
            case "client-to-other":
                return {
                    title: "Remove Client Assignment",
                    description:
                        "You're about to remove the client from this session.\n\nThis will:\n" +
                        "• Hide all parts (no one will see them until a client is assigned)\n" +
                        "• Clear part connections and similarity matches\n" +
                        "• Delete all part images\n\n" +
                        "You can assign a client later to restore access.",
                };
            case "other-to-client":
                return {
                    title: "Assign Client to Session",
                    description:
                        `You're about to assign ${newClientDisplay} to this session.\n\nThis will:\n` +
                        "• Make session parts visible in their parts list\n" +
                        "• Automatically match with similar existing parts\n" +
                        "• Generate images based on their settings",
                };
            default:
                return {
                    title: "Confirm Change",
                    description: "Are you sure you want to make this change?",
                };
        }
    };

    const handleCancel = () => {
        // Reset temp values (use "other" for IDs, null for emails - matches openDialog)
        setTempPractitionerId(practitionerId ?? "other");
        setTempClientId(clientId ?? "other");
        setTempPractitionerEmail(practitionerEmail ?? null);
        setTempClientEmail(clientEmail ?? null);
        setIsDialogOpen(false);
    };

    // If the practitioner changes, update the local values
    useEffect(() => {
        setTempPractitionerId(practitionerId ?? "other");
        setTempPractitionerEmail(practitionerEmail ?? null);
    }, [practitionerId, practitionerEmail]);

    // If the client changes, update the local values
    useEffect(() => {
        setTempClientId(clientId ?? "other");
        setTempClientEmail(clientEmail ?? null);
    }, [clientId, clientEmail]);

    // AIDEV-NOTE: Both reasons a participants row is non-interactive. Public
    // viewers have no rights; example-session viewers share the row with every
    // other practitioner, so reassigning its client would move it for all of them.
    const isLocked = isPublicSession || readOnly;

    const renderParticipantText = () => {
        // AIDEV-NOTE: Fall back to the client's NAME before "Unknown Client".
        // Practitioner-created placeholder clients routinely have a name and no
        // email, as does the shared onboarding demo client — and that surface is
        // meant to look finished, so "Unknown Client" there is especially bad.
        if (!practitionerEmail && !clientEmail) {
            return (
                <span
                    className="text-gray-500"
                    onClick={isLocked ? undefined : openDialog}
                >
                    {clientName?.trim()
                        ? `Practitioner & ${clientName.trim()}`
                        : "Practitioner & Unknown Client"}
                </span>
            );
        }

        // AIDEV-NOTE: For public sessions, render read-only text without click handlers
        return (
            <div onClick={isLocked ? undefined : openDialog} className="">
                <span
                    className={clsx(
                        "transition-colors",
                        !isLocked && "cursor-pointer",
                        !isLocked && "border-b border-dotted border-gray-300",
                        practitionerEmail
                            ? "text-md font-thinner text-brand-forest-800 hover:text-brand-forest-800 hover:bg-transparent "
                            : "text-gray-500  hover:text-gray-700",
                    )}
                >
                    {practitionerEmail || "Unknown Practitioner"}
                </span>
                <span className="mx-2">guiding</span>
                <span
                    className={clsx(
                        "transition-colors",
                        !isLocked && "cursor-pointer",
                        !isLocked && "border-b border-dotted border-gray-300",
                        clientEmail
                            ? "text-md font-thinner text-brand-forest-800 hover:text-brand-forest-800 hover:bg-transparent "
                            : "text-gray-500 hover:text-gray-700",
                    )}
                >
                    {clientEmail || "an Unknown Client"}
                </span>
            </div>
        );
    };

    return (
        <>
            <div className="flex items-center">{renderParticipantText()}</div>

            <Dialog
                open={isDialogOpen}
                onOpenChange={(open) => {
                    if (!open) handleCancel();
                }}
                analyticsId="session-participants-dialog"
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Session Participants</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <PractitionerSelector
                            selectedPractitionerId={selectorPractitionerId}
                            onChange={(id, email) => {
                                setTempPractitionerId(id);
                                setTempPractitionerEmail(email ?? null);
                            }}
                            label="Practitioner"
                        />
                        <ClientSelector
                            selectedClientId={selectorClientId}
                            onChange={(id, email) => {
                                setTempClientId(id);
                                setTempClientEmail(email ?? null);
                            }}
                            label="Client"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleSave}
                            size="sm"
                            isLoading={isLoading}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AIDEV-NOTE: Confirmation dialog for client reassignment */}
            <AlertDialog
                open={showConfirmation}
                onOpenChange={(open) => {
                    if (!open) handleConfirmationCancel();
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        {(() => {
                            const content = getConfirmationContent();
                            return (
                                <>
                                    <AlertDialogTitle>
                                        {content.title}
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        {content.description}
                                    </AlertDialogDescription>
                                </>
                            );
                        })()}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleConfirmationCancel}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                executeSubmit();
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
